import React, { useState, useEffect } from "react";
import {
  Button,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/system";
import { useNavigate } from "react-router-dom";
import api from "../api/api";

const colors = {
  black: "#111111",
  black2: "#1c1c1c",
  maroon: "#7B2D26",
  maroonLight: "#A94442",
  gold: "#E7C27D",
  white: "#FFFFFF",
  text: "#D8D8D8",
};

const Section = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${colors.black}, ${colors.black2})`,
  padding: "70px 8%",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "60px",
  overflow: "hidden",

  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    padding: "50px 20px",
    textAlign: "center",
  },
}));

const ImageBox = styled(Box)(({ theme }) => ({
  flex: 1,
  display: "flex",
  justifyContent: "center",

  "& img": {
    width: "100%",
    maxWidth: 480,
    borderRadius: 18,
    objectFit: "cover",
    border: `4px solid ${colors.maroon}`,
    boxShadow: "0 20px 40px rgba(0,0,0,.45)",
    transition: ".4s",

    "&:hover": {
      transform: "scale(1.03)",
    },
  },
}));

const ContentBox = styled(Box)(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: "22px",

  [theme.breakpoints.down("md")]: {
    alignItems: "center",
  },
}));

const ButtonGroup = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: 15,
  marginTop: 10,

  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    width: "100%",
  },
}));

const ApplyButton = styled(Button)(({ theme }) => ({
  background: colors.maroon,
  color: "#fff",
  padding: "13px 32px",
  borderRadius: 8,
  fontWeight: 700,
  fontSize: 15,
  textTransform: "none",
  transition: ".3s",

  "&:hover": {
    background: colors.maroonLight,
    transform: "translateY(-2px)",
  },

  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
}));

const AnnouncementButton = styled(Button)(({ theme }) => ({
  background: "transparent",
  color: colors.gold,
  border: `2px solid ${colors.gold}`,
  padding: "13px 30px",
  borderRadius: 8,
  fontWeight: 700,
  textTransform: "none",
  transition: ".3s",

  "&:hover": {
    background: colors.gold,
    color: colors.black,
  },

  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
}));

const AdmissionSession = () => {
  const [announcement, setAnnouncement] = useState(null);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchAnnouncement = async () => {
      try {
        const response = await api.get("/api/announcements/");

        if (response.data.length > 0) {
          setAnnouncement(response.data[0]);
        }
      } catch (err) {
        console.error(err);
      }

      setLoading(false);
    };

    fetchAnnouncement();
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          py: 8,
          background: colors.black,
        }}
      >
        <CircularProgress sx={{ color: colors.maroon }} />
      </Box>
    );
  }

  if (!announcement) return null;

  return (
    <Section>

      <ImageBox>
        <img
          src={announcement.image}
          alt={announcement.title}
        />
      </ImageBox>

      <ContentBox>

        <Typography
          sx={{
            color: colors.white,
            fontWeight: 800,
            fontSize: {
              xs: "2rem",
              md: "3rem",
            },
            lineHeight: 1.2,
          }}
        >
          {announcement.title}{" "}
          <Box
            component="span"
            sx={{
              color: colors.gold,
            }}
          >
            {announcement.highlight}
          </Box>
        </Typography>

        <Typography
          sx={{
            color: colors.text,
            fontSize: "1.05rem",
            lineHeight: 1.9,
          }}
        >
          {announcement.description}
        </Typography>

        <ButtonGroup>

          <ApplyButton variant="contained">
            {announcement.button_text}
          </ApplyButton>

          <AnnouncementButton
            variant="outlined"
            onClick={() => navigate("/schoolAnnouncement")}
          >
            School Announcement
          </AnnouncementButton>

        </ButtonGroup>

      </ContentBox>

    </Section>
  );
};

export default AdmissionSession;